import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchTrainingCoursesAsync, fetchConfigAsync, resetTrainingCourse } from "../../redux/onboarding-slice";
import { isEmpty, throttle } from "lodash";
import i18next from "i18next";
import { CircularProgress } from "@mui/material";
import { ErrorScreen } from "../../../../components/error-screen/error-screen";
import { useNavigate } from "react-router-dom";
import { HeaderComponent } from "../../../../components/header/header.component";
import { color } from "../../../../mui-theme/palette";
import { useQuery } from "../../../../utils/hooks/use-query";
import { convertLanguageKey } from "../../../../utils/lang";
import { selectOnboardingCourses } from "../../redux/selector";
import { OnboardingTrainingCourse } from "../../types";
import { useCanGoBack } from "../../../../utils/hooks/use-can-go-back";
import { ModuleSection, OnboardingCourseResourceType, SectionItem, StatusEnum } from "./type";
import { MenuList } from "./menu";
import useThrottledTouchEnd from "../../hooks/use-throtted-touch-end";

type MenuScreenProps = {};

export const CourseMenuScreen = (props: MenuScreenProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const query = useQuery();
  const candidateId = query.get("candidateId");
  const lang = query.get("lang") ?? "en";
  useThrottledTouchEnd();

  const navigate = useNavigate();
  const [data, setData] = useState<ModuleSection[] | undefined>(undefined);
  const courses = useSelector(selectOnboardingCourses);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    setLoading(true);
    if (!candidateId) return setLoading(false);
    await dispatch<any>(fetchTrainingCoursesAsync({ candidateId }));
    await dispatch<any>(resetTrainingCourse());
    setLoading(false);
  }, [candidateId, dispatch]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useEffect(() => {
    if (isEmpty(courses)) {
      return;
    }
    setData(
      courses?.map((course: OnboardingTrainingCourse) => {
        const lessonAndQuizes: SectionItem[] = [];
        for (const lesson of course.lessons) {
          lessonAndQuizes.push({
            name: lesson.code,
            type: OnboardingCourseResourceType.Lesson,
            title: lesson.name,
            data: lesson,
            status: lesson.status as keyof typeof StatusEnum,
          });
          if (lesson.quiz) {
            lessonAndQuizes.push({
              name: lesson.quiz.code,
              title: lesson.quiz.name,
              type: OnboardingCourseResourceType.LessonQuiz,
              data: lesson.quiz,
              status: lesson.quiz.status as keyof typeof StatusEnum,
            });
          }
        }
        if (course.quiz) {
          lessonAndQuizes.push({
            name: course.quiz.code,
            title: course.quiz.name,
            type: OnboardingCourseResourceType.CourseQuiz,
            data: course.quiz,
            status: course.quiz.status as keyof typeof StatusEnum,
          });
        }
        return {
          title: course.name,
          name: course.code,
          data: lessonAndQuizes,
          expanded: course.status !== "COMPLETED",
          status: course.status as keyof typeof StatusEnum,
          denominator: lessonAndQuizes.length,
          numerator: lessonAndQuizes.filter((item) =>
            [StatusEnum.COMPLETED, StatusEnum.PASSED].includes(item.status as any),
          ).length,
        };
      }),
    );
  }, [courses]);

  const throttledTouchEnd = useCallback(
    throttle(() => {
      window.ReactNativeWebView?.postMessage?.('{ "action" : "touchend"}');
    }, 1000),
    [],
  );
  useEffect(() => {
    dispatch<any>(fetchConfigAsync());
    document.addEventListener("touchend", throttledTouchEnd);
  }, []);
  useEffect(() => {
    i18next.changeLanguage(convertLanguageKey(lang));
  }, [lang]);
  const canGoBack = useCanGoBack();
  return (
    <div className="fixed overflow-hidden overscroll-y-none h-screen flex flex-col">
      <HeaderComponent
        title={t("onboarding_training")}
        onBack={() => {
          if (canGoBack()) {
            navigate(-1);
          }
        }}
      />
      {
        <div className={`w-screen flex flex-1 overflow-auto`}>
          {loading ? (
            <div className={`h-screen flex flex-1 items-center justify-center`}>
              {/* <CircularProgress sx={{ color: color.majorRed }} /> */}
            </div>
          ) : isEmpty(data) ? (
            <ErrorScreen statusCode="-1" />
          ) : (
            <>
              <div className={`w-screen flex flex-1 flex-col`}>
                <div className="flex-1 flex-col bg-gray-100">
                  <MenuList sections={data} />
                </div>
              </div>
            </>
          )}
        </div>
      }
    </div>
  );
};
